import { usePostHog } from "posthog-js/react";
import type React from "react";
import { Fragment } from "react";
import { Link } from "react-router";

import type { MentionDto } from "@/api/types";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { urlify } from "@/helpers/text";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";

interface MentionContentProps {
  content: string;
  contentTemplate?: string;
  mentions?: MentionDto[];
  source: "post" | "comment";
}

const mentionTokenRegex = /\{\{mention:([^}]+)}}/g;

/** Renders structured mention tokens as tracked detail links while keeping unavailable targets readable. */
export function MentionContent({ content, contentTemplate, mentions, source }: MentionContentProps): React.ReactNode {
  const slug = useSlug();
  const postHog = usePostHog();
  if (!contentTemplate || !mentions?.length) {
    return <MultiLineContent shouldHighlightLinks>{content}</MultiLineContent>;
  }

  const displayTemplate = unfurlDeepLinkFromContent(contentTemplate).content;
  const byId = new Map(mentions.map((mention) => [mention.id, mention]));
  const matches = [...displayTemplate.matchAll(mentionTokenRegex)];
  if (matches.some((match) => !byId.has(match[1]))) {
    return <MultiLineContent shouldHighlightLinks>{content}</MultiLineContent>;
  }
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.index == null) {
      continue;
    }

    parts.push(<Fragment key={`content-${cursor}`}>{urlify(displayTemplate.slice(cursor, match.index))}</Fragment>);
    const mention = byId.get(match[1])!;
    if (!mention.targetId) {
      parts.push(
        <span key={`${mention.id}-unavailable`} className="text-text-secondary">
          {mention.text}
        </span>,
      );
    } else if (mention.targetType === "user") {
      parts.push(
        <Link
          key={mention.id}
          className="font-medium text-text-brand-secondary hover:underline"
          to={routes.users.details({ slug, id: mention.targetId })}
          onClick={() => postHog.capture("community_mention_clicked", { source, targetType: mention.targetType })}
        >
          {mention.text}
        </Link>,
      );
    } else {
      parts.push(
        <Link
          key={mention.id}
          className="font-medium text-text-brand-secondary hover:underline"
          to={routes.companies.edit({ slug, id: mention.targetId })}
          onClick={() => postHog.capture("community_mention_clicked", { source, targetType: mention.targetType })}
        >
          {mention.text}
        </Link>,
      );
    }
    cursor = match.index + match[0].length;
  }
  parts.push(<Fragment key={`content-${cursor}`}>{urlify(displayTemplate.slice(cursor))}</Fragment>);

  return <MultiLineContent>{parts}</MultiLineContent>;
}
